import Container from "@mui/material/Container";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import React, { useEffect, useReducer, useState } from "react";
import {
  GetLensReviewDocument,
  GetQuestionnaireTableDocument,
  useGetQuestionnaireQuery,
  useUpdateQuestionnaireTableMutation,
} from "../../../generated";
import { Button, Typography } from "@mui/material";
import Loader from "../../common/Loader";
import { useLazyQuery } from "@apollo/client";
import AnswerQuestionnaireIacModal from "./AnswerQuestionnaireIacModal";
import QuestionIac from "./QuestionIac";
import { useSubmitIacEmail } from "../../../hooks/useSubmitIacEmail";
import StyledTableRow from "../../common/StyledTableRow";
import StyledTableCell from "../../common/StyledTableCell";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";

interface WorkloadsStageDictionary {
  [key: string]: string;
}

interface EmailSubmittedDictionary {
  [key: string]: boolean;
}

interface RiskCountDictionary {
  [key: string]: number;
}

interface RiskCount {
  hriCount: number;
  initialHriCount: number;
}

interface AnswerQuestionnaireIacProps {
  organisation: any;
  wafrArn: string;
  createdWorkloads: any;
  riskCounts: RiskCount;
  halfStageHriCount: number;
  riskCountsDict: any;
  firstRiskCountsDict: any;
  setRiskCountsDict: any;
  stateStage: any;
  setStateStage: any;
  checkedGenAI: boolean;
  changeTab: (index: number) => void;
  setUnlockSteps: React.Dispatch<React.SetStateAction<{
    step2: boolean; step3: boolean; step4: boolean 
  }>>;
  process: string;
}

function createData(
  sort_order: number,
  id: string,
  description: string,
  pillar: string,
  info_link: string,
  lens: string,
  status: string,
  updateStatus: string,
  reducedHri: number
) {
  return { sort_order, id, description, pillar, info_link, lens, status, updateStatus, reducedHri };
}

type RowQuestion = {
  sort_order: number;
  id: string;
  description: string;
  pillar: string;
  info_link: string;
  lens: string;
  status: string;
  updateStatus: string;
  reducedHri: number;
};

// Define the shape of your answer state and action
type AnswerState = Record<string, string[]>;

type AnswerAction = 
  | { type: "setAnswer"; questionId: string; payload: string[] }
  // You can extend this later with other action types if needed

function answerReducer(state: AnswerState, action: AnswerAction): AnswerState {
  switch (action.type) {
    case "setAnswer":
      return {
        ...state,
        [action.questionId]: action.payload,
      };
    default:
      return state;
  }
}

type DisabledState = Record<string, boolean[]>; // questionId -> array of per-workload disabled states

export default function AnswerQuestionnaireIac({
  organisation,
  wafrArn,
  createdWorkloads,
  riskCounts,
  halfStageHriCount,
  riskCountsDict,
  firstRiskCountsDict,
  setRiskCountsDict,
  stateStage,
  setStateStage,
  checkedGenAI,
  changeTab,
  setUnlockSteps,
  process,
}: AnswerQuestionnaireIacProps) {
  const [rows, setRows] = useState<Array<RowQuestion>>([]);
  const [emailSubmitted, setEmailSubmitted] = useState<EmailSubmittedDictionary>({});
  const [questionnaireAnswer, setQuestionnaireAnswer] = useState({});
  const [questionnaireSpinner, setQuestionnaireSpinner] = useState(false);
  const [answerMap, dispatchAnswer] = useReducer(answerReducer, {});
  const [disabledMap, setDisabledMap] = useState<DisabledState>({});

  // This handles the pillars being collapsible 
  const [expandedPillars, setExpandedPillars] = useState<Record<string, boolean>>({});

  const { data, loading } = useGetQuestionnaireQuery({
    variables: {
      arn: wafrArn,// value for 'arn'
    },
    context: {
      apiName: "well_architected",
    },
  });
  
  const { submitemail_mutation } = useSubmitIacEmail({ wafrArn, emailSubmitted, setEmailSubmitted, checkedGenAI });

  const [getLensReview] = useLazyQuery(GetLensReviewDocument,{
    context: {
      apiName: "well_architected",
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [updateQuestionnaireTableMutation] = useUpdateQuestionnaireTableMutation();
  
  const [ getQuestionnaireTable ] = useLazyQuery(GetQuestionnaireTableDocument,{
    context: { apiName: "well_architected" },
    fetchPolicy: "network-only"
  });

  const addWorkloadStageKeyValuePairs = (newPairs: WorkloadsStageDictionary) => {
    setStateStage((stateStage: any) => ({
      ...stateStage,
      ...newPairs
    }));
  };

  const addRiskKeyValuePairs = (newPairs: RiskCountDictionary) => {
    setRiskCountsDict((riskCountsDict: any) => ({
      ...riskCountsDict,
      ...newPairs
    }));
  };

  const addEmailSubmittedKeyValuePairs = (newPairs: EmailSubmittedDictionary) => {
    setEmailSubmitted((emailSubmitted: any) => ({
      ...emailSubmitted,
      ...newPairs
    }));
};

  const getHriResult = async (workloadid: string) => {
    /**
     * Get Hri Result
     * @param workloadid Well-Architected Workload Id
     */
    let totalHri = 0;
    const lensReview = await getLensReview({
      variables: {
        arn: wafrArn,
        workloadid,
        lens: "wellarchitected",
      },
    });
    let hri = 0;
    hri = lensReview?.data?.getLensReview?.PillarReviewSummaries?.map(
      (element: any) => {
        return element?.RiskCounts?.HIGH;
      }
    ).reduce((pre: number, cur: number) => {
      return pre + cur;
    }, 0);
    totalHri += hri;

    if (checkedGenAI) {
      const lensReview = await getLensReview({
        variables: {
          arn: wafrArn,
          workloadid,
          lens: "arn:aws:wellarchitected::aws:lens/genai",
        },
      });
      const hri = lensReview?.data?.getLensReview?.PillarReviewSummaries?.map(
        (element: any) => {
          return element?.RiskCounts?.HIGH;
        }
      ).reduce((pre: number, cur: number) => {
        return pre + cur;
      }, 0);
      totalHri += hri;
    }
    return totalHri;
  };

  //Handle that locks step 3
  const handleUnlockSteps = () => {
    setUnlockSteps(prevState => ({
      ...prevState,
      step3: false, // Lock step 3
      step4: true,
    }));
  };

  useEffect(() => {
    if (!loading && data?.getQuestionnaire) {
      handleUnlockSteps();
      const filtered = data.getQuestionnaire.filter((q: any) => 
        checkedGenAI || q.lens !== "arn:aws:wellarchitected::aws:lens/genai"
      );

      const mappedRows = filtered.map((q: any, index: number) =>
        createData(index + 1, q.id, q.description, q.pillar, q.info_link, q.lens, "", "INITIAL", 0)
      );
      setRows(mappedRows);
      setDisabledMap((prev) => {
        const newMap = { ...prev };
        if (data?.getQuestionnaire) {
          data.getQuestionnaire.forEach((q: any) => {
            if (!newMap[q.id]) {
              newMap[q.id] = new Array(Object.keys(createdWorkloads).length).fill(false);
            }
          });
        }
        return newMap;
      });
      const fetchAllWorkloads = async () => {
        const fetchPromises = Object.entries(createdWorkloads).map(async ([_key, value]) => {
          const workloadid = typeof value === "string" ? value : "";
          const hri = await getHriResult(workloadid);
          const { data: questionnaireData } = await getQuestionnaireTable({
            variables: { arn: wafrArn, workloadid },
          });

          const response = JSON.parse(questionnaireData.getQuestionnaireTable) as Record<string, string>;
          setQuestionnaireAnswer(prev => ({ ...prev, ...response }));

          Object.entries(response).forEach(([questionId, statusValue]) => {
            dispatchAnswer({
              type: "setAnswer",
              questionId,
              payload: [statusValue], // assuming one value per workload for now
            });
          });

          if (hri !== undefined) {
            addRiskKeyValuePairs({ [workloadid]: hri });
            addEmailSubmittedKeyValuePairs({ [workloadid]: false });
          }
        });
        await Promise.all(fetchPromises);
        setQuestionnaireSpinner(true); //Only after ALL workloads are fetched
      }
      fetchAllWorkloads();
    }
  }, [data, loading, createdWorkloads]);
  
  if(loading){
    window.scrollTo({ top: 0, behavior: 'smooth' }) //scroll to the top of the page
  }

  /**
   * Update Questionnaire Table
   * @param workloadid Well-Architected Workload Id
   * @param questionid Question Id
   * @param answer Answer Yes or No
   */
  const updateQuestionnaireTable = async (workloadid: string, questionid: string, answer: string) =>{
    await updateQuestionnaireTableMutation({
      variables: {
        arn: wafrArn,
        workloadid,// value for 'workloadid'
        questionid,// value for 'questionid'
        answer,// value for 'answer'
      },
      context: {
        apiName: "well_architected",
      },
    });
  }

  /**
   * Update Table Row
   * @param index row index number
   * @param row row question
   * @param workloadid Well-Architected Workload Id
   */
  const updateRow = async (index: number, row: RowQuestion, workloadid: string) => {
    const hri = await getHriResult(workloadid);
    if (hri !== undefined) {
      addRiskKeyValuePairs({[workloadid]:hri})
    }
    updateQuestionnaireTable(workloadid, row.id, row.status);
    if (riskCounts.hriCount <= halfStageHriCount / 2 && stateStage[workloadid] !== "HALF_STAGE") {
      addWorkloadStageKeyValuePairs({[workloadid]:"HALF_STAGE"});
      if(emailSubmitted[workloadid] === false){
        await Promise.all([
          submitemail_mutation(workloadid),
        ]);
      }
    }
  };

  /**
   * csv lines
   * @param lines csv line 
   * @returns lines
   */
  const csvLines = (lines: any) => {
    return lines
      .map((e: any) => {
        return e.join(",");
      })
      .join("\r\n");
  };

  /**
   * Export CSV
   */
  const exportCsv = () => {
    const header = ['ID', 'Description']
    Object.entries(createdWorkloads).forEach(([key, _value]) => {
      const name = key.split(`${organisation}-`).join('');
      header.push(name)
    })

    const dataRows = rows.map((row) => {
      const status = answerMap[row.id] ?? new Array(Object.keys(createdWorkloads).length).fill("");
      return [
        row.sort_order,
        row.pillar,
        JSON.stringify(row.description),
        ...status,
      ];
    });

    const csvFile = new Blob(
      [
        `${csvLines([header])}\r\n${csvLines(dataRows)}`
      ],
      { type: "text/csv" }
    );
    const utc = new Date().toJSON().slice(0,10);
    const name = `WAFR_People_Policy_and_Tools_${utc}.csv`;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(csvFile);

    link.setAttribute("visibility", "hidden");
    link.download = name;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // This handles the pillar grouping
  const groupedByPillar = rows.reduce((acc, row) => {
    if (!acc[row.pillar]) {
      acc[row.pillar] = [];
    }
    acc[row.pillar].push(row);
    return acc;
  }, {} as Record<string, Array<RowQuestion>>);
  
  // This sets the first pillar to be expanded and display rows under it as default
  useEffect(() => {
    if (rows.length > 0) {
      const grouped = rows.reduce((acc, row) => {
        if (!acc[row.pillar]) acc[row.pillar] = [];
        acc[row.pillar].push(row);
        return acc;
      }, {} as Record<string, Array<RowQuestion>>);

      const unique = Object.keys(grouped);
      const initial: Record<string, boolean> = {};
      unique.forEach((pillar, idx) => {
        initial[pillar] = idx === 0; // Open first
      });
      setExpandedPillars(initial);
    }
  }, [rows]);

  // Assist with toggling of the pillars
  const togglePillar = (pillar: string) => {
    setExpandedPillars(prev => ({
      ...prev,
      [pillar]: !prev[pillar],
    }));
  };

  return (
    <Container maxWidth="lg" component={Paper}>
      <Grid container sx={{ p: 1 }}>
        <Typography>
          NB: You will need to answer all questions in the one sitting due to
          the way that the workload is updated. If you would like any
          assistance, please reach out to secure@6pillars.ai
        </Typography>
      </Grid>
      <Grid item xs md={4}/>
      <Grid
        container
        sx={{ pb: 2, pt: 2, pl: 2 }}
        spacing={2}
        alignItems="center"
      >
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650, pt:10 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <StyledTableCell />
                <StyledTableCell>Question</StyledTableCell>
                {
                  Object.entries(createdWorkloads).map(([key, value]) => {
                    const workloadid = typeof value === "string" ? value: '';
                    const name = key.split(`${organisation}-`).join('');
                    const percentage = Math.round(
                      ((firstRiskCountsDict[workloadid]-riskCountsDict[workloadid])/firstRiskCountsDict[workloadid]) * 100
                    );
                    return(
                      <StyledTableCell key={key} align="center">
                        {name}
                        <br/>
                        {riskCountsDict[workloadid]}/{firstRiskCountsDict[workloadid]}({percentage}%) 
                      </StyledTableCell>
                    );
                  })
                }
                <StyledTableCell>Info</StyledTableCell>
                <StyledTableCell/>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow
                  key={1}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell colSpan={3}>
                    <Loader/>
                  </TableCell>
                </TableRow>
              )}
              {Object.entries(groupedByPillar).map(([pillar, pillarRows]) => (
                <React.Fragment key={pillar}>
                  <StyledTableRow onClick={() => togglePillar(pillar)}>                  
                    <StyledTableCell/>
                    <StyledTableCell scope="row" width={"75%"} sx={{ fontWeight: "bold" }}>
                      {pillar}
                    </StyledTableCell>
                    <StyledTableCell/>
                    <StyledTableCell/>
                    <StyledTableCell>
                      {
                        expandedPillars[pillar] ? 
                          <ExpandMoreOutlinedIcon fontSize="inherit" color="secondary" /> 
                        :
                          <ExpandLessOutlinedIcon fontSize="inherit" color="secondary" />
                      }
                    </StyledTableCell>
                  </StyledTableRow>
                  {expandedPillars[pillar] && pillarRows.map((row) => (
                    <QuestionIac
                      key={`Question-${row.id}`}
                      wafrArn={wafrArn}
                      createdWorkloads={createdWorkloads}
                      index={row.sort_order}
                      row={row}
                      updateRow={updateRow}
                      questionnaireAnswer={questionnaireAnswer}
                      process={process}
                      questionnaireSpinner={questionnaireSpinner}
                      // Pass the current selected answers from the reducer (or [] if not yet set)
                      selectedAnswers={answerMap[row.id] ?? []}
                      // Pass a callback that updates this question's answer state via dispatch
                      onUpdateAnswer={(newAnswers: string[]) =>
                        dispatchAnswer({ type: "setAnswer", questionId: row.id, payload: newAnswers })
                      }
                      disabledState={disabledMap[row.id]}
                      setDisabledState={(updatedArray: boolean[]) => {
                        setDisabledMap(prev => ({
                          ...prev,
                          [row.id]: updatedArray,
                        }));
                      }}
                      checkedGenAI={checkedGenAI}
                    />
                  ))}
                </React.Fragment>
              ))}
              <TableRow>
                <StyledTableCell/>
                <StyledTableCell/>
                {
                  Object.entries(createdWorkloads).map(([key, value]) => {
                    const workloadid = typeof value === "string" ? value: '';
                    const percentage = Math.round(
                      ((firstRiskCountsDict[workloadid]-riskCountsDict[workloadid])/firstRiskCountsDict[workloadid]) * 100
                    );
                    return(
                      <StyledTableCell key={`riskcounts-${key}`} align="center">
                        {riskCountsDict[workloadid]}/{firstRiskCountsDict[workloadid]}({percentage}%) 
                      </StyledTableCell>
                    );
                  })
                }
                <StyledTableCell/>
                <StyledTableCell/>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: "flex", pt: 3, direction: "row" }}>
          <Grid item xs md={8}>
            <Typography sx={{ pl: 1, pr: 2 }}>
              Click to submit your Well-Architected Framework Review
            </Typography>
          </Grid>
          <Grid item xs md={2} sx={{pr: 4}}>
            <Button
              onClick={exportCsv}
              type="submit"
              variant="contained"
              disabled={!questionnaireSpinner} // <- disable when not ready
              sx={{
                width: 157,
                height: 40,
                bgcolor: "secondary.main",
                "&:hover": {
                  bgcolor: "secondary.main",
                  color: "secondary.contrastText",
                },
              }}
            >
              Download
            </Button>
          </Grid>
          <Grid item xs md={2} sx={{pl: 4}}>
            <AnswerQuestionnaireIacModal
              wafrArn={wafrArn}
              rows={rows}
              createdWorkloads={createdWorkloads}
              checkedGenAI={checkedGenAI}
              changeTab={changeTab}
            />
          </Grid>
        </Box>
      </Grid>
    </Container>
  );
}
